import { useTranslation } from "react-i18next";

import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { AdviceCategoryEnum } from "@krea/customer-features/types/loanApplication";
import { Alert } from "@krea/shared/design-system/alerts/Alert";

import Button from "../button";

import AttractivenessComponent from "./attractiveness-component/AttractivenessComponent";
import { WhatIsAdvisory } from "./what-is-advisory";
import { YourAdvisor } from "./your-advisor";

interface IAdviceSubmitModalContentProps {
  submitToLenders: () => void;
  goToAdvice: () => void;
}

export const DigitalAdviceSubmitModalContent = ({
  goToAdvice,
  submitToLenders,
}: IAdviceSubmitModalContentProps) => {
  const { t } = useTranslation();
  const { data } = useGetCachedCurrentLoanApplication();
  const { applicationImprovements } = data ?? {};

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <AttractivenessComponent className="tw-w-full" />

      <Alert variant="infoAlert" className="!tw-m-0 tw-w-full">
        <Alert.Description className="tw-font-semibold">
          {applicationImprovements?.length && applicationImprovements.length > 1
            ? t("iaq.applicationPreview.submit.digitalAdvice.description", {
                count: applicationImprovements.length,
              })
            : t("iaq.applicationPreview.submit.digitalAdvice.descriptionOne", {
                count: applicationImprovements?.length,
              })}
        </Alert.Description>
      </Alert>

      <Button size="lg" onClick={goToAdvice}>
        {t("iaq.applicationPreview.submit.digitalAdvice.cta")}
      </Button>
      <Button size="lg" variant="outlined" onClick={submitToLenders}>
        {t("iaq.applicationPreview.submit.continueWithoutImprovements")}
      </Button>
    </div>
  );
};

export const ManualAdviceSubmitModalContent = ({
  goToAdvice,
  submitToLenders,
}: IAdviceSubmitModalContentProps) => {
  const { t } = useTranslation();
  const { data } = useGetCachedCurrentLoanApplication();
  const { adviceCategory } = data ?? {};

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <YourAdvisor
        className="tw-rounded tw-border tw-border-mint-500"
        title={
          <p className="tw-mb-1 tw-font-semibold tw-text-gray-500">
            {t("iaq.applicationPreview.submit.manualAdvice.description")}
          </p>
        }
        isOnModal
      />

      <WhatIsAdvisory />

      <Button size="lg" onClick={goToAdvice}>
        {t("iaq.applicationPreview.submit.manualAdvice.cta")}
      </Button>
      {adviceCategory !== AdviceCategoryEnum.COMPLEX && (
        <Button size="lg" variant="outlined" onClick={submitToLenders}>
          {t("iaq.applicationPreview.submit.continueWithoutImprovements")}
        </Button>
      )}
    </div>
  );
};

export const RevenueAdviceSubmitModalContent = ({
  goToAdvice,
  submitToLenders,
}: IAdviceSubmitModalContentProps) => {
  const { t } = useTranslation();

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <AttractivenessComponent className="tw-w-full" />

      <Alert variant="infoAlert" className="!tw-m-0 tw-w-full">
        <Alert.Description className="tw-font-semibold">
          {t("iaq.applicationPreview.submit.revenueAdvice.description")}
        </Alert.Description>
      </Alert>

      <Button size="lg" onClick={goToAdvice}>
        {t("iaq.applicationPreview.submit.revenueAdvice.cta")}
      </Button>
      <Button size="lg" variant="outlined" onClick={submitToLenders}>
        {t("iaq.applicationPreview.submit.continueWithoutImprovements")}
      </Button>
    </div>
  );
};
